import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Ability } from '@casl/ability';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components';
import { dailyDiarySubject } from '../daily-diary-ability.util';
import { DIARY_ID_PARAM, DIARY_SECTION_PARAM } from '../daily-diary-mode.util';

/**
 * Mode B body: the diaries filed for ONE day, rendered the way
 * `app-daily-reports-list` renders that day's reports — a status-accented card
 * per record (author, filed-on timestamp, status) that expands into the
 * record's component sections.
 */
@Component({
  selector: 'app-daily-diary-day',
  templateUrl: './daily-diary-day.component.html',
  styleUrls: ['./daily-diary-day.component.scss'],
  standalone: false,
})
export class DailyDiaryDayComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId!: string;
  @Input() siteId!: string;
  @Input() date!: string;

  diaries: any[] = [];
  loading = false;
  selectedDiaryId = '';

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private ability: Ability,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.selectedDiaryId = qp[DIARY_ID_PARAM] || '';
        this.autoSelectFirst();
      });

    // A save from any of the focused right panels re-emits this.
    this.projectsService.dailyDiaryChanged
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchDay());

    this.fetchDay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['date'] && !changes['date'].firstChange)
      || (changes['siteId'] && !changes['siteId'].firstChange)) {
      this.fetchDay();
    }
  }

  fetchDay(): void {
    if (!this.siteId || !this.date) return;
    this.loading = true;
    // A different day starts fresh — the collapse applied to the old one.
    this.userCollapsed = false;

    // The list endpoint already returns weather, remarks (with pictures) and
    // createdBy for every row, so one call renders the whole day.
    this.projectsService.fetchDailyDiaries({
      siteId: this.siteId,
      startDate: this.date,
      endDate: this.date,
    }).pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          this.diaries = (resp?.data || [])
            .map((r: any) => ({ ...r, ...(r.attributes || {}) }))
            .filter((r: any) => moment(r.diary_date).isSame(moment(this.date), 'day'));
          // The floating + hides on an empty day: there is no diary to add a
          // component to, and a diary is started from the list instead.
          this.projectsService.diaryDayHasRecord.next(!!this.diaries.length);
          this.autoSelectFirst();
        },
        error: () => {
          this.loading = false;
          this.diaries = [];
          this.projectsService.diaryDayHasRecord.next(false);
        },
      });
  }

  /**
   * Open the first record of the day, as the reports list does — but only
   * when the user hasn't collapsed one. Without that check, collapsing the
   * chevron navigated to `diaryId: null`, which re-entered this and
   * immediately re-expanded the same card.
   */
  private autoSelectFirst(): void {
    if (this.selectedDiaryId || this.userCollapsed || !this.diaries.length) return;
    this.selectDiary(this.diaries[0]);
  }

  selectDiary(diary: any): void {
    const id = diary?.id || diary?.uuid;

    // Collapsing is local state, NOT a navigation. Navigating to
    // `diaryId: null` raced the `closeRightPanel` subscriber, which merges its
    // own query params off whatever is current and put the id straight back —
    // so the card re-expanded and could never be closed.
    if (this.selectedDiaryId === id && !this.userCollapsed) {
      this.userCollapsed = true;
      this.projectsService.closeRightPanel.next();
      return;
    }

    this.userCollapsed = false;
    if (this.selectedDiaryId === id) return;

    this.router.navigate([], {
      queryParams: {
        [DIARY_ID_PARAM]: id,
        [DIARY_SECTION_PARAM]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /** Disables the Submit button while a submit is in flight. */
  submitting = false;

  /** Set when the user collapsed a card; the template reads it. */
  userCollapsed = false;

  canDelete(diary: any): boolean {
    return diary?.status === 'draft' && this.ability.can('delete', dailyDiarySubject(diary));
  }

  /**
   * Draft submits; submitted RE-submits (the api allows both and pushes to
   * AASHTOWare again). `locked` is closed on the AASHTOWare side — nothing to
   * do from here.
   */
  canSubmit(diary: any): boolean {
    const status = diary?.status;
    if (status !== 'draft' && status !== 'submitted') return false;
    return this.ability.can('submit', dailyDiarySubject(diary));
  }

  submitLabel(diary: { status?: string }): string {
    return diary?.status === 'submitted' ? 'Resubmit' : 'Submit';
  }

  /** The kebab is Delete-only now that submitting is a button on the record. */
  hasMenu(diary: any): boolean {
    return this.canDelete(diary);
  }

  /**
   * Submitting is a button under the record, the way a daily report's workflow
   * action sits under the report. It used to be "Save & Submit" on the
   * whole-record create panel, which no longer exists.
   */
  submit(event: MouseEvent, diary: any): void {
    event?.stopPropagation?.();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        `${this.submitLabel(diary)} diary`,
        `${this.submitLabel(diary)} the diary for `
        + `${moment(diary.diary_date).format('MMM D, YYYY')}?<br/>`
        + 'It will be pushed to AASHTOWare.',
      ),
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.submitting = true;
      this.projectsService.submitDailyDiary({ diaryId: diary.id || diary.uuid })
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: () => {
            this.submitting = false;
            this.commonService.notification(
              diary?.status === 'submitted' ? 'Diary resubmitted' : 'Diary submitted',
              'success',
            );
            this.projectsService.closeRightPanel.next();
            this.projectsService.dailyDiaryChanged.next();
            this.fetchDay();
          },
          error: (err: any) => {
            this.submitting = false;
            this.commonService.errorNotification(err?.error?.error || 'Failed to submit diary');
          },
        });
    });
  }

  delete(event: MouseEvent, diary: any): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete diary',
        `Are you sure you want to delete the diary for ${moment(diary.diary_date).format('MMM D, YYYY')}?`,
      ),
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.projectsService.deleteDailyDiary({ diaryId: diary.id || diary.uuid })
        .pipe(takeUntil(this.onDestroy))
        .subscribe({
          next: () => {
            this.commonService.notification('Diary deleted', 'success');
            this.projectsService.closeRightPanel.next();
            this.router.navigate([], {
              queryParams: { [DIARY_ID_PARAM]: null, [DIARY_SECTION_PARAM]: null },
              queryParamsHandling: 'merge',
            }).then(() => this.fetchDay());
          },
          error: (err: any) => {
            this.commonService.errorNotification(err?.error?.error || 'Failed to delete diary');
          },
        });
    });
  }

  ngOnDestroy(): void {
    // Leaving the record view; the flag only describes an open day.
    this.projectsService.diaryDayHasRecord.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
